import type {
  Announcement,
  DiagramClarificationQuestion,
  Hint,
  Infographic,
  InfographicImage,
  LiveDiagram,
  Screenshot,
  SentimentSample,
  TranscriptLine,
  UseCase,
} from "@scoach/types";
import { type DocumentData, collection, doc, onSnapshot, orderBy, query } from "firebase/firestore";
import { useEffect } from "react";

import { getDb } from "../../../../lib/firestore.ts";
import { useLiveMeetingStore } from "../store.ts";

/**
 * Subscribes to the live-meeting Firestore subcollections via onSnapshot.
 * Uses Firebase's own WebSocket transport (always works behind Hosting).
 *
 *   meetings/{id}/transcript ordered by t (asc)
 *   meetings/{id}/hints      ordered by _at  (asc)
 *   meetings/{id}/sentiment  ordered by at (asc)
 *   meetings/{id}/followups/latest (single doc)
 */
export function useLiveMeeting(meetingId: string): void {
  const setConnection = useLiveMeetingStore((s) => s.setConnection);
  const setMeetingId = useLiveMeetingStore((s) => s.setMeetingId);
  const setStartedAt = useLiveMeetingStore((s) => s.setStartedAt);
  const setSttError = useLiveMeetingStore((s) => s.setSttError);
  const setAudioWarning = useLiveMeetingStore((s) => s.setAudioWarning);
  const setLivePartial = useLiveMeetingStore((s) => s.setLivePartial);
  const apply = useLiveMeetingStore((s) => s.applyServerMessage);
  const reset = useLiveMeetingStore((s) => s.reset);

  useEffect(() => {
    let unsubs: Array<() => void> = [];
    setMeetingId(meetingId);
    setStartedAt(Date.now());
    setConnection(true);

    try {
      const db = getDb();
      const meetingRef = doc(db, "meetings", meetingId);

      const unsubTranscript = onSnapshot(
        query(collection(meetingRef, "transcript"), orderBy("t", "asc")),
        (snap) => {
          for (const change of snap.docChanges()) {
            if (change.type === "removed") continue;
            const line = change.doc.data() as TranscriptLine & DocumentData;
            apply({ type: "transcript-final", line });
          }
        },
        (err) => {
          console.warn("[live] transcript listener error", err);
        },
      );
      unsubs.push(unsubTranscript);

      // Skip the FIRST snapshot: it replays every hint already stored for this
      // meeting (e.g. from a prior session), which would surface stale hints
      // before the rep has started this call. Only apply hints ADDED afterwards
      // — the live ones generated during this session.
      let hintsPrimed = false;
      const unsubHints = onSnapshot(
        query(collection(meetingRef, "hints"), orderBy("_at", "asc")),
        (snap) => {
          if (!hintsPrimed) {
            hintsPrimed = true;
            return;
          }
          for (const change of snap.docChanges()) {
            if (change.type === "removed") continue;
            const hint = change.doc.data() as Hint & DocumentData;
            apply({ type: "hint", hint });
          }
        },
        (err) => {
          console.warn("[live] hints listener error", err);
        },
      );
      unsubs.push(unsubHints);

      const unsubSentiment = onSnapshot(
        query(collection(meetingRef, "sentiment"), orderBy("at", "asc")),
        (snap) => {
          for (const change of snap.docChanges()) {
            if (change.type === "removed") continue;
            const sample = change.doc.data() as SentimentSample & DocumentData;
            apply({ type: "sentiment", sample });
          }
        },
        (err) => {
          console.warn("[live] sentiment listener error", err);
        },
      );
      unsubs.push(unsubSentiment);

      const unsubFollowups = onSnapshot(
        doc(meetingRef, "followups", "latest"),
        (snap) => {
          const data = snap.data() as { items?: string[] } | undefined;
          if (data?.items) apply({ type: "followups", items: data.items });
        },
        (err) => {
          console.warn("[live] followups listener error", err);
        },
      );
      unsubs.push(unsubFollowups);

      const addNote = useLiveMeetingStore.getState().addNote;

      const unsubAutoNotes = onSnapshot(
        query(collection(meetingRef, "autoNotes"), orderBy("_at", "asc")),
        (snap) => {
          for (const change of snap.docChanges()) {
            if (change.type !== "added") continue;
            const data = change.doc.data() as {
              t: string;
              text: string;
              source?: string;
            } & DocumentData;
            const current = useLiveMeetingStore.getState().notes;
            const exists = current.some((n) => n.text === data.text && n.t === data.t);
            if (!exists) {
              addNote({ t: data.t, text: data.text, source: "auto" });
            }
          }
        },
        (err) => {
          console.warn("[live] autoNotes listener error", err);
        },
      );
      unsubs.push(unsubAutoNotes);

      const unsubTips = onSnapshot(
        query(collection(meetingRef, "tips"), orderBy("_at", "desc")),
        (snap) => {
          for (const change of snap.docChanges()) {
            if (change.type === "removed") continue;
            const tip = change.doc.data() as {
              id: string;
              text: string;
              at: number;
            } & DocumentData;
            apply({ type: "tip", tip });
          }
        },
        (err) => {
          console.warn("[live] tips listener error", err);
        },
      );
      unsubs.push(unsubTips);

      const unsubLive = onSnapshot(
        doc(meetingRef, "live", "state"),
        (snap) => {
          const data = snap.data() as { sttError?: string; audioWarning?: string } | undefined;
          setSttError(data?.sttError ?? null);
          setAudioWarning(data?.audioWarning || null);
        },
        (err) => {
          console.warn("[live] state listener error", err);
        },
      );
      unsubs.push(unsubLive);

      const addInfographic = useLiveMeetingStore.getState().addInfographic;
      const unsubInfographics = onSnapshot(
        query(collection(meetingRef, "infographics"), orderBy("_at", "desc")),
        (snap) => {
          for (const change of snap.docChanges()) {
            if (change.type === "removed") continue;
            const ig = change.doc.data() as Infographic & DocumentData;
            addInfographic(ig);
          }
        },
        (err) => {
          console.warn("[live] infographics listener error", err);
        },
      );
      unsubs.push(unsubInfographics);

      const addInfographicImage = useLiveMeetingStore.getState().addInfographicImage;
      const unsubInfographicImages = onSnapshot(
        collection(meetingRef, "infographicImages"),
        (snap) => {
          console.log(
            "[live] infographicImages snapshot fired, changes:",
            snap.docChanges().length,
            "total:",
            snap.size,
          );
          for (const change of snap.docChanges()) {
            if (change.type === "removed") continue;
            const img = { id: change.doc.id, ...change.doc.data() } as InfographicImage &
              DocumentData;
            console.log("[live] infographicImage received:", img.id, img.imageUrl);
            addInfographicImage(img);
          }
        },
        (err) => {
          console.warn("[live] infographicImages listener error", err);
        },
      );
      unsubs.push(unsubInfographicImages);

      const addScreenshot = useLiveMeetingStore.getState().addScreenshot;
      const unsubScreenshots = onSnapshot(
        collection(meetingRef, "screenshots"),
        (snap) => {
          for (const change of snap.docChanges()) {
            if (change.type === "removed") continue;
            const ss = { id: change.doc.id, ...change.doc.data() } as Screenshot & DocumentData;
            addScreenshot(ss);
          }
        },
        (err) => {
          console.warn("[live] screenshots listener error", err);
        },
      );
      unsubs.push(unsubScreenshots);

      const addAnnouncement = useLiveMeetingStore.getState().addAnnouncement;
      const unsubAnnouncements = onSnapshot(
        query(collection(meetingRef, "announcements"), orderBy("_at", "desc")),
        (snap) => {
          for (const change of snap.docChanges()) {
            if (change.type === "removed") continue;
            const a = change.doc.data() as Announcement & DocumentData;
            addAnnouncement(a);
          }
        },
        (err) => {
          console.warn("[live] announcements listener error", err);
        },
      );
      unsubs.push(unsubAnnouncements);

      const addUseCase = useLiveMeetingStore.getState().addUseCase;
      const unsubUseCases = onSnapshot(
        query(collection(meetingRef, "useCases"), orderBy("_at", "desc")),
        (snap) => {
          for (const change of snap.docChanges()) {
            if (change.type === "removed") continue;
            const u = change.doc.data() as UseCase & DocumentData;
            addUseCase(u);
          }
        },
        (err) => {
          console.warn("[live] useCases listener error", err);
        },
      );
      unsubs.push(unsubUseCases);

      const addDiagram = useLiveMeetingStore.getState().addDiagram;
      const unsubDiagrams = onSnapshot(
        query(collection(meetingRef, "diagrams"), orderBy("_at", "asc")),
        (snap) => {
          for (const change of snap.docChanges()) {
            if (change.type === "removed") continue;
            const d = change.doc.data() as LiveDiagram & DocumentData;
            addDiagram(d);
          }
        },
        (err) => {
          console.warn("[live] diagrams listener error", err);
        },
      );
      unsubs.push(unsubDiagrams);

      const setDiagramClarifications = useLiveMeetingStore.getState().setDiagramClarifications;
      const unsubDiagramClarify = onSnapshot(
        doc(meetingRef, "diagramClarifications", "current"),
        (snap) => {
          const data = snap.data() as
            | { ready?: boolean; questions?: DiagramClarificationQuestion[] }
            | undefined;
          const questions = !data || data.ready ? null : (data.questions ?? null);
          setDiagramClarifications(questions && questions.length > 0 ? questions : null);
        },
        (err) => {
          console.warn("[live] diagram clarifications listener error", err);
        },
      );
      unsubs.push(unsubDiagramClarify);

      const unsubPartial = onSnapshot(
        doc(meetingRef, "live", "partial"),
        (snap) => {
          const data = snap.data() as { text?: string } | undefined;
          const text = (data?.text ?? "").trim();
          setLivePartial(text || null);
        },
        (err) => {
          console.warn("[live] partial listener error", err);
        },
      );
      unsubs.push(unsubPartial);
    } catch (err) {
      console.warn("[live] failed to attach Firestore listeners", err);
      setConnection(false);
      import("../../../../mocks/fixtures/live-meeting.ts")
        .then(({ MOCK_INFOGRAPHICS }) => {
          const store = useLiveMeetingStore.getState();
          for (const ig of MOCK_INFOGRAPHICS) store.addInfographic(ig);
        })
        .catch(() => {});
    }

    return () => {
      for (const u of unsubs) u();
      unsubs = [];
      setConnection(false);
      reset();
    };
  }, [
    meetingId,
    apply,
    reset,
    setConnection,
    setMeetingId,
    setStartedAt,
    setSttError,
    setAudioWarning,
    setLivePartial,
  ]);
}
